// @flow
// to be fixed in TRVX-11152
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';

import { localize, unescaped } from '@ta/common.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { Avatar } from '@ta/styleguide.avatar';
import { DeprecatedModalLink } from '@ta/overlays.modal';
import { Opener } from '@ta/overlays.managers';
import { Toaster } from '@ta/overlays.toast';
import { RouteConsumer, Link } from '@ta/common.routing';
import classNames from '@ta/common.classnames';
import Logging from '@ta/common.logging';
import { DisplayUntagIcon } from '@ta/social.actions';
import { EditMediaModal } from '@ta/media.upload-exports';
import { EditLinkPostModal } from '@ta/social.linkpost-upload';
import { escapeHtml } from '@ta/common.text-html';
import { InteractionLogger } from '@ta/tracking.interactions';
import { ContentDisplayDate, getDisplayDate } from '@ta/social.dates';
import {
  SectionOverflowMenu,
  type OverflowItemOverride,
  type SectionObject,
  type SectionOverflowItemType,
} from '@ta/social.section-overflow';

import { getDisplayName, MemberHeaderStats } from '@ta/social.member';

import styles from './MemberEventOnObjectBlock.less';

type Props = {
  actor: ?any,
  objectEventType:
    | 'onReview'
    | 'onFullReview'
    | 'onPhoto'
    | 'onFullPhoto'
    | 'onMediaBatch'
    | 'onVideo'
    | 'onForumPost'
    | 'onLinkPost'
    | 'onRepost'
    | 'onRoomTip'
    | 'onTrip'
    | 'onLocationTopic'
    | 'onTopicPosting',
  object: any,
  deleteObject: ?() => void,
  untagObject?: ?() => void,
  loggedInUserId?: ?string,
  /** We hide certain parts of the block if it's embedded in something else (rn used for re-posts) */
  isEmbedded: boolean,
  displayDate?: string,
  renderMemberActions: boolean,
  renderAvatar: boolean,
  overflowMenuObjectForType?: { [SectionOverflowItemType]: SectionObject },
};

type ObjectProps = {
  object: any,
  actor: ?any,
  /** We hide certain parts of the block if it's embedded in something else (rn used for re-posts) */
  isEmbedded: boolean,
  displayDate?: string,
};

type ObjectWithGeoScopeProps = {
  objectProps: ObjectProps,
  geoScope: number,
  /** We hide certain parts of the block if it's embedded in something else (rn used for re-posts) */
  isEmbedded: boolean,
};

type OverflowMenuProps = {
  parentProps: Props,
  editUgcModalName: string,
  overrideItems: OverflowItemOverride,
};

type ObjectWithOverflowProps = {
  objectProps: ObjectProps,
  overflowMenuProps: OverflowMenuProps,
};

const getLocalAndForeignUserProfileLink = (type: ?string, object: any, actor: any) => {
  if (!featureIsEnabled('show_foreign_member_profile') || !type) {
    return actor?.route?.url || '';
  }
  let usePrefixForForeignMember;
  let isLocalProvider;
  let prefix;
  switch (type) {
    case 'Review':
      usePrefixForForeignMember =
        object.provider?.isLocalProvider !== undefined && object.provider?.isLocalProvider !== null;
      isLocalProvider = object.provider?.isLocalProvider === true;
      /* foreign member is only enabled for DaoDao */
      prefix = usePrefixForForeignMember && !isLocalProvider ? 'https://www.tripadvisor.com' : '';
      return actor?.route?.url ? `${prefix}${actor?.route?.url}` : '';
    default:
      break;
  }
  return actor?.route?.url || '';
};

const getMessageTranslations = (type: string, params: any, object: any, actor: any) => {
  if (object.repostedObject) {
    switch (type) {
      case 'Review':
        return localize(
          'member_event_reposted_review',
          params,
        ); /* localize('member_event_reposted_review') reposted a review */
      case 'Photo':
        return localize(
          'member_event_reposted_photo',
          params,
        ); /* localize('member_event_reposted_photo') reposted a photo */
      case 'MediaBatch':
        return localize(
          'member_event_reposted_photo_v2',
          params,
        ); /* localize('member_event_reposted_photo_v2') reposted photos */
      case 'Video':
        return localize(
          'member_event_reposted_video',
          params,
        ); /* localize('member_event_reposted_video') reposted a video */
      case 'ForumPost':
        return localize(
          'member_event_reposted_forumpost',
          params,
        ); /* localize('member_event_reposted_forumpost') reposted a forum */
      case 'LinkPost':
        return localize(
          'member_event_reposted_linkpost',
          params,
        ); /* localize('member_event_reposted_linkpost') reposted a link */
      case 'Trip':
        return localize(
          'member_event_reposted_Trip',
          params,
        ); /* localize('member_event_reposted_Trip') reposted a Trip */
      case 'ApiLocationTopic':
        // TODO: Replace with final copy
        return `<a class="${params[0]}" href="${params[1]}">${params[2]}</a> asked a question`;
      case 'ApiTopicPosting':
        // TODO: Replace with final copy
        return `Answer from <a class="${params[0]}" href="${params[1]}">${params[2]}</a>`;
      default:
        break;
    }
  } else {
    const tripOwnerId = object.__typename === 'Trip' && object.owner && object.owner.id;
    const actorId = actor && actor.userId;
    switch (type) {
      case 'Review':
        return localize(
          'member_event_on_review',
          params,
        ); /* localize('member_event_on_review') wrote a review */
      case 'RoomTip':
        return localize(
          'member_event_on_tip',
          params,
        ); /* localize('member_event_on_tip') wrote a tip */
      case 'Photo':
        return localize(
          'member_event_on_photo',
          params,
        ); /* localize('member_event_on_photo') posted a photo */
      case 'MediaBatch':
        return localize(
          'feed_batched_photos_header_v2',
          params,
        ); /* localize('feed_batched_photos_header_v2') posted {4} photo(s) */
      case 'Video':
        return localize(
          'member_event_on_video',
          params,
        ); /* localize('member_event_on_video') posted a video */
      case 'ForumPost':
        if (object.id === object.parentId) {
          return localize(
            'member_event_on_forumpost_question',
            params,
          ); /* localize('member_event_on_forumpost_question') posted a question */
        }
        return localize(
          'member_event_on_forumpost_reply',
          params,
        ); /* localize('member_event_on_forumpost_reply') posted a reply */
      case 'LinkPost':
        return localize(
          'member_event_on_linkpost',
          params,
        ); /* localize('member_event_on_linkpost') posted a link */
      case 'Trip':
        if (tripOwnerId === actorId) {
          return localize(
            'member_event_on_Trip',
            params,
          ); /* localize('member_event_on_Trip') created a Trip */
        }
        return localize(
          'trips_feed_trip_joined_v2',
          params,
        ); /* localize('trips_feed_trip_joined_v2') joined a Trip */
      case 'ApiLocationTopic':
        return localize('member_event_on_location_topic', params);
      case 'ApiTopicPosting':
        return localize('member_event_on_topic_posting', params);

      default:
        break;
    }
  }

  return '';
};

const getDetailPageMessageTranslations = (type: string, params: any) => {
  switch (type) {
    case 'Review':
      return localize(
        'member_event_on_review_with_date',
        params,
      ); /* localize('member_event_on_review_with_date') $user wrote a review $date */
    case 'RoomTip':
      return localize(
        'member_event_on_tip_with_date',
        params,
      ); /* localize('member_event_on_tip_with_date') $user wrote a tip $date */
    case 'Photo':
      return localize(
        'member_event_on_photo_with_date',
        params,
      ); /* localize('member_event_on_photo_with_date') $user posted a photo $date */
    case 'ApiLocationTopic':
      return localize(
        'member_event_on_question_with_date',
        params,
      ); /* localize('member_event_on_question_with_date') $user asked a question $date */
    default:
      break;
  }

  return '';
};

// Single source for actor/info/target messaging
const ActorEventMessage = ({ object, actor, isEmbedded, displayDate }: ObjectProps) => {
  const memberLinkClass = classNames(displayDate ? 'ui_header_link' : 'ui_link', styles.member, {
    verified: actor ? actor.isVerified : false,
  });
  const messageParams = {
    '0': memberLinkClass,
    '1': getLocalAndForeignUserProfileLink(object?.__typename, object, actor),
    '2': escapeHtml(getDisplayName(actor)),
    '3': displayDate ? getDisplayDate(displayDate) : null,
    '4':
      object?.__typename === 'Repost' ? object?.repostedObject?.media?.length : object?.numPhotos,
  };

  const actorId = actor && actor.userId;
  const target = object.repostedObject ? object.repostedObject.__typename : object.__typename;

  const message =
    displayDate && displayDate.length > 0
      ? getDetailPageMessageTranslations(target, messageParams)
      : getMessageTranslations(target, messageParams, object, actor);

  return (
    <InteractionLogger>
      {logUserClick => (
        <div
          className={styles.event_type}
          onClick={e => logUserClick(e, 'userClick', { userId: actorId })}
        >
          {!isEmbedded ? (
            unescaped(message)
          ) : (
            <Link className={memberLinkClass} href={actor?.route?.url}>
              {getDisplayName(actor)}
            </Link>
          )}
        </div>
      )}
    </InteractionLogger>
  );
};

// Reviews
const EventOnReview = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate || object.createdDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <InteractionLogger>
            {logFeedClick => (
              <Link
                href={object?.route?.url}
                className="ui_link"
                onClick={e => logFeedClick(e, 'feedClick')}
              >
                <ContentDisplayDate dateStr={displayDate} />
              </Link>
            )}
          </InteractionLogger>
        </span>
      </div>
    </React.Fragment>
  );
};

// Reviews (Full review on detail page -- so far only difference is to not to link to SUR)
const EventOnFullReview = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate || object.createdDate;

  return (
    <React.Fragment>
      <ActorEventMessage
        actor={actor}
        object={{ ...object, __typename: 'Review' }}
        displayDate={displayDate}
      />
      {actor && <MemberHeaderStats profile={actor} />}
    </React.Fragment>
  );
};

// Room Tip
const EventOnRoomTip = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate;

  return (
    <React.Fragment>
      <ActorEventMessage
        actor={actor}
        object={{ ...object, __typename: 'RoomTip' }}
        displayDate={displayDate}
      />
      {actor && <MemberHeaderStats profile={actor} />}
    </React.Fragment>
  );
};

// Embedded object
const EventOnEmbeddedObject = ({ object, actor }: ObjectProps) => {
  const displayDate =
    object.publishedDate || object.createdDate || object.uploadDate || object.created;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} isEmbedded />

      {displayDate && (
        <div className={styles.event_info}>
          <span className={styles.item}>
            <InteractionLogger>
              {logFeedClick => (
                <Link
                  href={object?.route?.url}
                  className="ui_link"
                  onClick={e => logFeedClick(e, 'feedClick')}
                >
                  <ContentDisplayDate dateStr={displayDate} />
                </Link>
              )}
            </InteractionLogger>
          </span>
        </div>
      )}
    </React.Fragment>
  );
};

// Photos
const EventOnPhoto = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate || object.uploadDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <InteractionLogger>
            {logFeedClick => (
              <DeprecatedModalLink
                name={`Photo_${object.id}`}
                href={null}
                username={object.userProfile ? object.userProfile.username : ''}
                className="ui_link"
                key={`Photo_${object.id}`}
                onClick={e => logFeedClick(e, 'feedClick')}
              >
                <ContentDisplayDate dateStr={displayDate} />
              </DeprecatedModalLink>
            )}
          </InteractionLogger>
        </span>
      </div>
    </React.Fragment>
  );
};

// Photos (Full photo on detail page -- so far only difference is to not to link from date)
const EventOnFullPhoto = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate || object.uploadDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} displayDate={displayDate} />
      {actor && <MemberHeaderStats profile={actor} />}
    </React.Fragment>
  );
};

// MediaBatch
const EventOnMediaBatch = ({ object, actor, isEmbedded }: ObjectProps) => {
  const displayDate = object.createdDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} isEmbedded={isEmbedded} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <ContentDisplayDate dateStr={displayDate} />
        </span>
      </div>
    </React.Fragment>
  );
};

// Videos
const EventOnVideo = ({ objectProps: { object, actor } }: ObjectWithGeoScopeProps) => {
  const displayDate = object.publishedDate || object.uploadDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <InteractionLogger>
            {logFeedClick => (
              <Link className="ui_link" onClick={e => logFeedClick(e, 'feedClick')}>
                <ContentDisplayDate dateStr={displayDate} />
              </Link>
            )}
          </InteractionLogger>
        </span>
      </div>
    </React.Fragment>
  );
};

// Forums
const EventOnForumPost = ({ object, actor }: ObjectProps) => {
  const displayDate = object.publishedDate;
  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <InteractionLogger>
            {logFeedClick => (
              <Link
                href={object?.route?.url}
                className="ui_link"
                onClick={e => logFeedClick(e, 'feedClick')}
              >
                <ContentDisplayDate dateStr={displayDate} />
              </Link>
            )}
          </InteractionLogger>
        </span>
      </div>
    </React.Fragment>
  );
};

// Link Posts
const EventOnLinkPost = ({ objectProps: { object, actor } }: ObjectWithGeoScopeProps) => {
  const displayDate = object.publishedDate;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <ContentDisplayDate dateStr={displayDate} />
        </span>
      </div>
    </React.Fragment>
  );
};

// Reposts
const EventOnRepost = ({ object, actor }: ObjectProps) => {
  const displayDate = object.created;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={object} />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <ContentDisplayDate dateStr={displayDate} />
        </span>
      </div>
    </React.Fragment>
  );
};

// Trips
const EventOnTrip = ({ objectProps: { object, actor } }: ObjectWithGeoScopeProps) => {
  let displayDate = object.created; // Default to created date
  if (object.owner && actor && actor.userId === object.owner.id) {
    displayDate = object.firstPublished; // Use firstPublished if I'm the owner
  } else {
    const actorCollaborator =
      actor &&
      object.collaborators &&
      object.collaborators.find(item => item.user && item.user.id === actor.userId);
    if (actorCollaborator) {
      displayDate = actorCollaborator.publicallyJoined; // Use publicallyJoined if I'm a collaborator
    }
  }

  return (
    <React.Fragment>
      <ActorEventMessage
        actor={actor}
        object={object}
        /* When the embedded flag is set then just the member avatar and name is displayed and we're using it,
           if we don't have all the data to decide if the actor is the owner. */
        isEmbedded={!(actor && actor.userId && object.owner && object.owner.id)}
      />

      <div className={styles.event_info}>
        <span className={styles.item}>
          <InteractionLogger>
            {logFeedClick => (
              <Link
                href={object?.route?.url}
                className="ui_link"
                onClick={e => logFeedClick(e, 'feedClick')}
              >
                <ContentDisplayDate dateStr={displayDate} />
              </Link>
            )}
          </InteractionLogger>
        </span>
      </div>
    </React.Fragment>
  );
};

const EventOnLocationTopic = ({ object, actor }: ObjectProps) => {
  const timestamp = object.timePublished || object.timeSubmitted;

  return (
    <React.Fragment>
      <ActorEventMessage
        actor={actor}
        object={{ ...object, __typename: 'ApiLocationTopic' }}
        displayDate={new Date(timestamp).toISOString()}
      />
      {actor && <MemberHeaderStats profile={actor} />}
    </React.Fragment>
  );
};

const EventOnTopicPosting = ({
  objectProps: { object, actor },
  overflowMenuProps,
}: ObjectWithOverflowProps) => {
  const timestamp = object.timePublished || object.timeSubmitted;

  return (
    <React.Fragment>
      <ActorEventMessage actor={actor} object={{ ...object, __typename: 'ApiTopicPosting' }} />
      {timestamp ? (
        <div className={styles.event_info}>
          <span className={styles.item}>
            <ContentDisplayDate dateStr={new Date(timestamp).toISOString()} />
            {' | '}
            <OverflowMenu {...overflowMenuProps} />
          </span>
        </div>
      ) : null}
    </React.Fragment>
  );
};

const getContentId = (object: any) => object.id || object.postId || object.repostId;

const OverflowMenu = ({ parentProps, editUgcModalName, overrideItems }: OverflowMenuProps) => (
  <Opener name={editUgcModalName}>
    {openEdit => (
      <SectionOverflowMenu
        sectionObject={parentProps.object}
        sectionObjectForType={parentProps.overflowMenuObjectForType}
        sectionActor={parentProps.actor}
        onEdit={openEdit}
        overrideItems={overrideItems}
        deleteObject={parentProps.deleteObject}
        untagObject={parentProps.untagObject}
      />
    )}
  </Opener>
);

class MemberEventOnObjectBlock extends React.Component<Props> {
  static defaultProps = {
    deleteObject: null,
    isEmbedded: false,
    displayDate: '',
    renderMemberActions: true,
    renderAvatar: true,
  };

  // This needs to be a class component so it doesnt Math.random on every re-render
  editUgcModalName = `ugcEditModal_${this.props.object.__typename}_${getContentId(
    this.props.object,
  )}:${Math.random()}`;

  render() {
    const contentType = this.props.object.__typename;

    let objectEventRender;
    let avatarSize = 'default';
    let primaryOverflowEnabled = true;

    if (this.props.isEmbedded) {
      objectEventRender = (
        <EventOnEmbeddedObject object={this.props.object} actor={this.props.actor} />
      );
    } else
      switch (this.props.objectEventType) {
        case 'onTopicPosting':
          avatarSize = 'xtra-small';
          primaryOverflowEnabled = false;
          break;
        default:
          break;
      }

    const overrideItems =
      this.props.actor && !this.props.actor.isMe
        ? {
            FOLLOW: true,
            UNTAG: !!DisplayUntagIcon(this.props.object, this.props.loggedInUserId || undefined),
          }
        : {
            FOLLOW: false,
            UNTAG: !!DisplayUntagIcon(this.props.object, this.props.loggedInUserId || undefined),
          };

    let editComponent = null;
    if (contentType === 'Photo' || contentType === 'Video') {
      editComponent = (
        <Toaster>
          {launchToast => (
            <EditMediaModal
              modalName={this.editUgcModalName}
              mediaObject={this.props.object}
              onSubmit={() => {
                launchToast({ node: localize('ugc_post_updated') });
              }}
            />
          )}
        </Toaster>
      );
    }
    if (contentType === 'LinkPost') {
      editComponent = (
        <Toaster>
          {launchToast => (
            <EditLinkPostModal
              modalName={this.editUgcModalName}
              linkPost={this.props.object}
              onSubmit={() => {
                launchToast({ node: localize('ugc_post_updated') });
              }}
            />
          )}
        </Toaster>
      );
    }

    const overflowMenuProps = {
      parentProps: this.props,
      editComponent,
      editUgcModalName: this.editUgcModalName,
      overrideItems,
    };

    if (this.props.isEmbedded) {
      objectEventRender = (
        <EventOnEmbeddedObject object={this.props.object} actor={this.props.actor} />
      );
    } else
      switch (this.props.objectEventType) {
        case 'onReview':
          objectEventRender = <EventOnReview object={this.props.object} actor={this.props.actor} />;
          break;
        case 'onFullReview':
          objectEventRender = (
            <EventOnFullReview object={this.props.object} actor={this.props.actor} />
          );
          break;
        case 'onRoomTip':
          objectEventRender = (
            <EventOnRoomTip object={this.props.object} actor={this.props.actor} />
          );
          break;
        case 'onPhoto':
          objectEventRender = <EventOnPhoto object={this.props.object} actor={this.props.actor} />;
          break;
        case 'onFullPhoto':
          objectEventRender = (
            <EventOnFullPhoto object={this.props.object} actor={this.props.actor} />
          );
          break;
        case 'onMediaBatch':
          objectEventRender = (
            <EventOnMediaBatch
              object={this.props.object}
              actor={this.props.actor}
              isEmbedded={this.props.isEmbedded}
            />
          );
          break;
        case 'onVideo':
          objectEventRender = (
            <RouteConsumer>
              {({ route }) => (
                <EventOnVideo objectProps={this.props} geoScope={parseInt(route.geo, 10)} />
              )}
            </RouteConsumer>
          );
          break;
        case 'onForumPost':
          objectEventRender = (
            <EventOnForumPost object={this.props.object} actor={this.props.actor} />
          );
          break;
        case 'onLinkPost':
          objectEventRender = (
            <RouteConsumer>
              {({ route }) => (
                <EventOnLinkPost objectProps={this.props} geoScope={parseInt(route.geo, 10)} />
              )}
            </RouteConsumer>
          );
          break;
        case 'onRepost':
          objectEventRender = <EventOnRepost object={this.props.object} actor={this.props.actor} />;
          break;
        case 'onTrip':
          objectEventRender = (
            <RouteConsumer>
              {({ route }) => (
                <EventOnTrip objectProps={this.props} geoScope={parseInt(route.geo, 10)} />
              )}
            </RouteConsumer>
          );
          break;
        case 'onLocationTopic':
          objectEventRender = (
            <EventOnLocationTopic object={this.props.object} actor={this.props.actor} />
          );
          break;
        case 'onTopicPosting':
          objectEventRender = (
            <EventOnTopicPosting objectProps={this.props} overflowMenuProps={overflowMenuProps} />
          );
          break;
        default:
          Logging.error('objectEventRender Error - object is required.');
      }

    return (
      <div className={styles.member_event_block}>
        {!this.props.isEmbedded && this.props.renderAvatar && (
          <InteractionLogger>
            {logUserClick => (
              <Avatar
                photo={this.props.actor?.avatar}
                display="inline"
                target="_self"
                href={getLocalAndForeignUserProfileLink(
                  this.props.objectEventType === 'onFullReview' ? 'Review' : null,
                  this.props.object,
                  this.props.actor,
                )}
                withBorder
                onClick={e =>
                  logUserClick(e, 'userClick', {
                    userId: this.props.actor?.userId,
                  })
                }
                size={avatarSize}
              />
            )}
          </InteractionLogger>
        )}
        <div className={styles.event_wrap}>
          {this.props.renderMemberActions ? (
            <div className={styles.member_actions}>
              {!this.props.isEmbedded && (
                <React.Fragment>
                  {editComponent}
                  {primaryOverflowEnabled && (
                    <OverflowMenu {...overflowMenuProps} overrideItems={overrideItems} />
                  )}
                </React.Fragment>
              )}
            </div>
          ) : (
            <div className={styles.member_actions} />
          )}
          {objectEventRender}
        </div>
      </div>
    );
  }
}

const defaultProps = {
  isEmbedded: false,
  displayDate: '',
};
ActorEventMessage.defaultProps = defaultProps;
EventOnReview.defaultProps = defaultProps;
EventOnFullReview.defaultProps = defaultProps;
EventOnRoomTip.defaultProps = defaultProps;
EventOnPhoto.defaultProps = defaultProps;
EventOnFullPhoto.defaultProps = defaultProps;
EventOnMediaBatch.defaultProps = defaultProps;
EventOnVideo.defaultProps = defaultProps;
EventOnForumPost.defaultProps = defaultProps;
EventOnLinkPost.defaultProps = defaultProps;
EventOnRepost.defaultProps = defaultProps;
EventOnEmbeddedObject.defaultProps = defaultProps;
EventOnTrip.defaultProps = defaultProps;
EventOnLocationTopic.defaultProps = defaultProps;
EventOnTopicPosting.defaultProps = defaultProps;

export default MemberEventOnObjectBlock;
